/**
 * Android's Back (the button or the edge swipe; Safari's back swipe too) closes what is open
 * inside the app instead of leaving it. The app has no pages of its own in the history, so
 * Back would leave it — and close the installed app — even in the middle of a race.
 *
 * While something takes Back (an open dialog, the game screen), an entry of ours sits on top
 * of the page's history. Back pops it and runs the handler registered last (close the dialog,
 * open or close the in-game menu), and the entry goes back on top for the next Back. When
 * nothing takes Back any more, the entry is taken off again, so Back in the room leaves the
 * app at the first press. Chrome skips entries a page adds before the player ever touched it,
 * so the entry waits for the first tap or key press.
 */
import { GESTURE_EVENTS } from '../audio/context';

/** Marks our history entries (history.state). */
const STATE_KEY = 'cubeRushBack';

/** What the back button needs from the browser (a fake in tests). */
export interface BackWindow {
  readonly history: Pick<History, 'state' | 'pushState' | 'back'>;
  readonly navigator: { readonly userActivation?: { readonly hasBeenActive: boolean } };
  readonly document: Pick<Document, 'addEventListener' | 'removeEventListener'>;
  addEventListener(type: 'popstate', listener: () => void): void;
  removeEventListener(type: 'popstate', listener: () => void): void;
}

export class BackButton {
  private readonly handlers: Array<() => void> = [];
  private win: BackWindow | null = null;
  /** Our entry is on top of the history: Back stays in the app. */
  private armed = false;
  /** The next popstate is our own history.back() taking the entry off. */
  private ownPop = false;

  /** Wires the browser; returns the uninstall function. */
  install(win: BackWindow): () => void {
    this.win = win;
    this.armed = false;
    this.ownPop = false;
    const onPopState = () => this.onPopState();
    const onGesture = () => this.arm();
    const options: AddEventListenerOptions = { capture: true, passive: true };
    win.addEventListener('popstate', onPopState);
    for (const type of GESTURE_EVENTS) win.document.addEventListener(type, onGesture, options);
    this.arm();
    return () => {
      win.removeEventListener('popstate', onPopState);
      for (const type of GESTURE_EVENTS) win.document.removeEventListener(type, onGesture, options);
      this.win = null;
    };
  }

  /** While registered, Back calls `handler` (the one registered last) instead of leaving. */
  register(handler: () => void): () => void {
    this.handlers.push(handler);
    this.arm();
    return () => {
      const index = this.handlers.lastIndexOf(handler);
      if (index < 0) return;
      this.handlers.splice(index, 1);
      if (this.handlers.length === 0) this.disarm();
    };
  }

  /**
   * Does what Back does, without the history (a gamepad's B): runs the handler registered
   * last. False when nothing takes Back — then nothing happens (B never leaves the app).
   */
  press(): boolean {
    const handler = this.handlers[this.handlers.length - 1];
    if (!handler) return false;
    handler();
    return true;
  }

  private arm(): void {
    const win = this.win;
    if (!win || this.armed || this.handlers.length === 0) return;
    if (win.navigator.userActivation?.hasBeenActive === false) return;
    win.history.pushState({ [STATE_KEY]: true }, '');
    this.armed = true;
  }

  /** Nothing takes Back any more: our entry comes off (never anybody else's). */
  private disarm(): void {
    const win = this.win;
    if (!win || !this.armed) return;
    this.armed = false;
    const state: unknown = win.history.state;
    if (typeof state === 'object' && state !== null && STATE_KEY in state) {
      this.ownPop = true;
      win.history.back();
    }
  }

  private onPopState(): void {
    if (this.ownPop) {
      this.ownPop = false;
      return;
    }
    if (!this.armed) return;
    this.armed = false;
    this.handlers[this.handlers.length - 1]?.();
    this.arm();
  }
}

/** The app's back button (installed by createApp; without it, registering does nothing). */
export const backButton = new BackButton();
